import { StructuredTextDocument } from "datocms-structured-text-to-html-string";
import React from "react";
import { isPastEvent } from "../../helpers/dateTimeDatoString";
import { EventPageUiLabels } from "../../templates/event";
import DateTime from "../DateTime";
import { PageContextProps } from "../Layout";
import RegistrationButtons from "../RegistrationButtons";
import SectionWrapper from "../SectionWrapper";
import StructuredTextForPageTitle from "../StructuredTextForPageTitle";
import Tag from "../Tag";

interface Props {
  title: Queries.EventPageQuery["datoCmsEvent"]["title"];
  from: Queries.EventPageQuery["datoCmsEvent"]["from"];
  to: Queries.EventPageQuery["datoCmsEvent"]["to"];
  location: Queries.EventPageQuery["datoCmsEvent"]["location"][0];
  registrationLink: Queries.EventPageQuery["datoCmsEvent"]["sectionRegistration"][0]["registrationLink"];
  additionalInformation: Queries.EventPageQuery["datoCmsEvent"]["sectionRegistration"]["0"]["additionalInformation"];
  groupServices: Queries.EventPageQuery["datoCmsEvent"]["allGroupServicePagesReferencingThisEvent"][0]["groupService"][];
  uiLabels: EventPageUiLabels;
  locale: PageContextProps["locale"];
  isBannerVisible?: boolean;
}

const EventPageSectionPageTitle = ({
  title,
  from,
  to,
  location,
  registrationLink,
  additionalInformation,
  groupServices,
  uiLabels,
  locale,
  isBannerVisible = false,
}: Props) => {
  const data = title.value as StructuredTextDocument;
  const isEventPast = isPastEvent(from, to);
  const externalGroupServices = groupServices?.filter(
    (groupService) => !groupService.internalService
  );

  // Location
  const getLocationText = (
    location: Queries.DatoCmsEventPagePhysicalLocation
  ) => {
    return `${location.name}, ${location.streetAndNumber}, ${location.zipcode} ${location.city}, ${location.country.label}`;
  };
  const locationText =
    location.__typename === "DatoCmsEventPagePhysicalLocation"
      ? getLocationText(
          location as unknown as Queries.DatoCmsEventPagePhysicalLocation
        )
      : uiLabels.online;

  return (
    <SectionWrapper
      additionalContentClassName="page-title"
      additionalWrapperClassName={isBannerVisible && "!pt-0"}
    >
      {/* Event ended tag */}
      {isEventPast ? (
        <Tag
          text={uiLabels.eventEndedTag}
          color="red"
          additionalClassName="mb-12 text-xs inline-block"
        />
      ) : null}

      {/* Event title */}
      <StructuredTextForPageTitle data={data} />
      {/* Event meta */}
      <div className="mt-4 text-sm text-gray-700 sm:text-base lg:text-lg">
        <DateTime from={from} to={to} locale={locale} />
        <div>{locationText}</div>
      </div>
      {/* Buttons */}
      {!isEventPast ? (
        <RegistrationButtons
          location={location}
          registrationLink={registrationLink}
          additionalInformation={additionalInformation}
          uiLabels={uiLabels}
        />
      ) : null}
      {/* Group Service chips */}
      {externalGroupServices && externalGroupServices.length > 0 ? (
        <div className="mt-16 flex flex-wrap gap-2 text-xs">
          {externalGroupServices.map((groupService) => (
            <Tag
              link={groupService.pagePath}
              color="teal"
              text={groupService.name}
              key={groupService.name}
            />
          ))}
        </div>
      ) : null}
    </SectionWrapper>
  );
};

export default EventPageSectionPageTitle;
